import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getActiveEmergencyNotices } from '@entities/emergency-notice'
import { getMockEmergencyNotice } from './mockNotice'
import { EmergencyNotice, EmergencyNoticeActiveResponse } from './types'

interface UseEmergencyNoticeOptions {
  canShowNotice?: boolean
  dismissedNoticeIds?: Set<number>
}

const sortVisibleNotices = (notices: EmergencyNotice[]) =>
  [...notices].sort((a, b) => b.priority - a.priority || b.startAt - a.startAt)

const isVisibleNotice = (notice: EmergencyNotice, now: number) =>
  notice.status !== 'disabled' &&
  notice.startAt <= now &&
  now < notice.endAt

const isDisplayableNotice = (
  notice: EmergencyNotice,
  now: number,
  { canShowNotice = true, dismissedNoticeIds = new Set<number>() }: UseEmergencyNoticeOptions = {}
) => {
  if (!isVisibleNotice(notice, now)) return false
  if (notice.type === 'maintenance') return true
  return canShowNotice && !dismissedNoticeIds.has(notice.idx)
}

const getDisplayableNotices = (
  notices: EmergencyNotice[],
  now: number,
  options?: UseEmergencyNoticeOptions
) =>
  sortVisibleNotices(notices.filter(notice => isDisplayableNotice(notice, now, options)))

const pickVisibleNotice = (
  notices: EmergencyNotice[],
  now: number,
  options?: UseEmergencyNoticeOptions
) => {
  const visibleNotices = getDisplayableNotices(notices, now, options)

  return (
    visibleNotices.find(notice => notice.type === 'maintenance') ??
    visibleNotices.find(notice => notice.type === 'notice') ??
    null
  )
}

const resolveServerNow = (
  data: EmergencyNoticeActiveResponse | undefined,
  dataUpdatedAt: number,
  fallbackNow: number
) => {
  if (!data?.serverTime || dataUpdatedAt <= 0) return fallbackNow

  return data.serverTime + Math.floor((Date.now() - dataUpdatedAt) / 1000)
}

const hasDisplayableNotice = (
  data: EmergencyNoticeActiveResponse | undefined,
  dataUpdatedAt: number,
  options?: UseEmergencyNoticeOptions
) => {
  if (!data) return false

  const now = resolveServerNow(data, dataUpdatedAt, Math.floor(Date.now() / 1000))
  return data.notices.some(notice => isDisplayableNotice(notice, now, options))
}

export const useEmergencyNotice = (
  clientNow: number,
  options: UseEmergencyNoticeOptions = {}
) => {
  const query = useQuery<EmergencyNoticeActiveResponse>({
    queryKey: ['emergency-notice', 'active'],
    queryFn: getActiveEmergencyNotices,
    refetchInterval: query =>
      hasDisplayableNotice(query.state.data, query.state.dataUpdatedAt, options)
        ? 60 * 1000
        : 60 * 60 * 1000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  const serverNow = useMemo(() => {
    return resolveServerNow(query.data, query.dataUpdatedAt, clientNow)
  }, [clientNow, query.data?.serverTime, query.dataUpdatedAt])

  const notice = useMemo(() => {
    const mockNotice = getMockEmergencyNotice()
    const notices = mockNotice ? [mockNotice] : query.data?.notices ?? []

    return pickVisibleNotice(notices, serverNow, options)
  }, [query.data?.notices, serverNow, options])

  return {
    notice,
    serverNow,
    hasVisibleNotice: hasDisplayableNotice(query.data, query.dataUpdatedAt, options),
    isLoading: query.isLoading,
  }
}
